import { createContext, MutableRefObject, PropsWithChildren, useCallback, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

import { getSessionStorageValue, preJoinedSessionKey, setSessionStorageValue } from 'hooks/useSessionState';
import { SetState } from 'utils/types';

export enum JoinErrorEnum {
  BANNED = 'banned',
  NOT_INVITED = 'not_invited',
  COULD_NOT_FIND_THE_ROOM = 'could_not_find_the_room',
  UNKNOWN = 'unknown',
}

export enum ConnectionStatus {
  DISCONNECTED = 'disconnected',
  CONNECTING = 'connecting',
  CONNECTED = 'connected',
}

export type SfuConnectionStateContextType = {
  connectionStatus: ConnectionStatus;
  setConnectionStatus: SetState<ConnectionStatus>;
  token?: string;
  setToken: SetState<string | undefined>;
  isParticipantDisconnectedRef: MutableRefObject<boolean>;
  isParticipantTerminatedRef: MutableRefObject<boolean>;
  clearConnectionState: () => void;
  shouldJoinMeetingOnRoom: boolean;
  setShouldJoinMeetingOnRoom: SetState<boolean>;
  setJoinError: SetState<JoinErrorEnum | undefined>;
  joinError: JoinErrorEnum | undefined;
  isPreJoined: boolean;
  setIsPreJoined: SetState<boolean>;
};

export const SfuConnectionStateContext = createContext<SfuConnectionStateContextType | null>(null);

export const SfuConnectionStateProvider = ({ children }: PropsWithChildren) => {
  const { roomId } = useParams();
  const [connectionStatus, setConnectionStatus] = useState(ConnectionStatus.DISCONNECTED);
  const [shouldJoinMeetingOnRoom, setShouldJoinMeetingOnRoom] = useState(false);

  // `isPreJoined` is persisted per-room in sessionStorage so the pre-joined
  // waiting state (and its Figma teal status card) survives provider remounts
  // triggered by Matrix sync / navigation (BUG-007). Without this, the card
  // would flip back to the `clock` variant and lose the Figma color.
  const preJoinedKey = roomId ? preJoinedSessionKey(roomId) : null;
  const [isPreJoined, setIsPreJoinedState] = useState<boolean>(() =>
    preJoinedKey ? getSessionStorageValue<boolean>(preJoinedKey) ?? false : false
  );
  const setIsPreJoined = useCallback<SetState<boolean>>(
    (value) => {
      setIsPreJoinedState((prev) => {
        const next = typeof value === 'function' ? (value as (p: boolean) => boolean)(prev) : value;
        if (preJoinedKey) setSessionStorageValue(preJoinedKey, next);
        return next;
      });
    },
    [preJoinedKey]
  );
  const [joinError, setJoinError] = useState<JoinErrorEnum>();
  const [token, setToken] = useState<string>();
  const isParticipantDisconnectedRef = useRef(false);
  const isParticipantTerminatedRef = useRef(false);

  const clearConnectionState = useCallback(() => {
    setToken(undefined);
  }, []);

  const state: SfuConnectionStateContextType = {
    connectionStatus,
    setConnectionStatus,
    token,
    setToken,
    isParticipantDisconnectedRef,
    isParticipantTerminatedRef,
    clearConnectionState,
    shouldJoinMeetingOnRoom,
    setShouldJoinMeetingOnRoom,
    joinError,
    setJoinError,
    isPreJoined,
    setIsPreJoined,
  };

  return <SfuConnectionStateContext.Provider value={state}>{children}</SfuConnectionStateContext.Provider>;
};
